import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import {
    Loader2,
    Send,
    Clock,
    Paperclip,
    Sparkles,
    X
} from "lucide-react";
import {
    listMailboxes,
    listMailTemplates,
    sendMessage,
    saveDraft,
    cancelMessage,
    aiMailDraft,
    recentLeadMail,
    listMailLibrary,
    uploadMailFile,
    mergeMailVars
} from "../../services/crmServices/mailService";

const TONES = [
    { id: "default", label: "Draft" },
    { id: "shorter", label: "Shorter" },
    { id: "warmer", label: "Warmer" },
    { id: "formal", label: "Formal" },
    { id: "translate", label: "Translate" }
];

function quoteHtml(msg) {
    const who = msg.from?.name || msg.from?.address || "";
    const body = msg.html || (msg.text ? `<p>${String(msg.text).replace(/\n/g, "<br/>")}</p>` : "");
    return `<p></p><blockquote style="border-left:3px solid #d1d5db;margin:12px 0;padding-left:12px;color:#4b5563">${who ? `<p><strong>${who}</strong> wrote:</p>` : ""}${body}</blockquote>`;
}

export function ComposeEmailDialog({ open, onClose, lead, replyTo, draft, onSent }) {
    const [mailboxes, setMailboxes] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [library, setLibrary] = useState([]);
    const [mailboxId, setMailboxId] = useState("");
    const [to, setTo] = useState("");
    const [cc, setCc] = useState("");
    const [showCc, setShowCc] = useState(false);
    const [subject, setSubject] = useState("");
    const [html, setHtml] = useState("");
    const [mode, setMode] = useState("visual");
    const [scheduledAt, setScheduledAt] = useState("");
    const [sendLater, setSendLater] = useState(false);
    const [followUp, setFollowUp] = useState(false);
    const [followHours, setFollowHours] = useState(48);
    const [followSubject, setFollowSubject] = useState("");
    const [followHtml, setFollowHtml] = useState("");
    const [attachments, setAttachments] = useState([]);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(false);
    const [aiLoading, setAiLoading] = useState(false);
    const [aiNote, setAiNote] = useState("");
    const [templateId, setTemplateId] = useState("");
    const editorRef = useRef(null);
    const fileRef = useRef(null);

    const leadId = lead?._id || replyTo?.leadId?._id || replyTo?.leadId || draft?.leadId || null;

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const [mbRes, tplRes, libRes] = await Promise.all([
                    listMailboxes(),
                    listMailTemplates(),
                    listMailLibrary().catch(() => ({ data: [] }))
                ]);
                if (cancelled) return;
                const boxes = mbRes.data || [];
                setMailboxes(boxes);
                setTemplates(tplRes.data || []);
                setLibrary(libRes.data || []);
                const def = boxes.find((b) => b.isDefault) || boxes[0];
                setMailboxId(draft?.mailboxId || replyTo?.mailboxId || def?._id || "");
            } catch (err) {
                toast.error(err.response?.data?.message || "Could not load mailboxes");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, draft?.mailboxId, replyTo?.mailboxId]);

    useEffect(() => {
        if (!open) return;
        if (draft) {
            setTo((draft.to || []).map((a) => a.address).join(", "));
            setCc((draft.cc || []).map((a) => a.address).join(", "));
            setShowCc(!!draft.cc?.length);
            setSubject(draft.subject || "");
            setHtml(draft.html || "");
            setAttachments(draft.attachments || []);
            return;
        }
        if (replyTo) {
            const inbound = replyTo.direction === "inbound";
            const dest = inbound
                ? replyTo.from?.address
                : (replyTo.to || []).map((a) => a.address).join(", ");
            setTo(dest || lead?.email || "");
            setSubject(replyTo.subject?.match(/^re:/i) ? replyTo.subject : `Re: ${replyTo.subject || ""}`);
            setHtml(quoteHtml(replyTo));
            setAttachments([]);
            return;
        }
        setTo(lead?.email || "");
        setCc("");
        setShowCc(false);
        setSubject("");
        setHtml("");
        setAttachments([]);
        setScheduledAt("");
        setSendLater(false);
        setFollowUp(false);
        setTemplateId("");
        setAiNote("");
    }, [open, lead, replyTo, draft]);

    useEffect(() => {
        if (!open || !leadId) {
            setRecent([]);
            return;
        }
        recentLeadMail(leadId)
            .then((res) => setRecent(res.data || []))
            .catch(() => setRecent([]));
    }, [open, leadId]);

    useEffect(() => {
        if (mode === "visual" && editorRef.current && editorRef.current.innerHTML !== (html || "")) {
            editorRef.current.innerHTML = html || "";
        }
    }, [mode, open, html]);

    const applyTemplate = (id) => {
        setTemplateId(id);
        const tpl = templates.find((t) => t._id === id);
        if (!tpl) return;
        setSubject(mergeMailVars(tpl.subject, lead || {}));
        const body = mergeMailVars(tpl.html, lead || {});
        setHtml(body);
        if (editorRef.current) editorRef.current.innerHTML = body;
    };

    const syncHtmlFromEditor = () => {
        if (mode === "visual" && editorRef.current) {
            setHtml(editorRef.current.innerHTML);
        }
    };

    const payload = () => {
        syncHtmlFromEditor();
        const body = mode === "visual" && editorRef.current ? editorRef.current.innerHTML : html;
        return {
            mailboxId,
            leadId,
            to,
            cc: showCc ? cc : "",
            subject,
            html: body,
            attachments,
            scheduledAt: sendLater && scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
            inReplyTo: replyTo?.messageId || "",
            threadKey: replyTo?.threadKey || "",
            replyToMessageId: replyTo?._id || undefined,
            followUp: followUp
                ? {
                      enabled: true,
                      afterHours: followHours,
                      subject: followSubject || `Re: ${subject}`,
                      html: followHtml || ""
                  }
                : undefined
        };
    };

    const handleSend = async () => {
        if (!mailboxId) return toast.error("Add a mailbox first (Inbox → Mailboxes)");
        if (!to.trim()) return toast.error("Recipient is required");
        if (!subject.trim()) return toast.error("Subject is required");
        setLoading(true);
        try {
            const res = await sendMessage(payload());
            if (res.status !== 200) throw new Error(res.message || "Send failed");
            const msg = res.data;
            if (msg?.status === "queued" && msg?._id) {
                toast.success("Sending in 10 seconds", {
                    action: {
                        label: "Undo",
                        onClick: async () => {
                            try {
                                await cancelMessage(msg._id);
                                toast.success("Send cancelled — saved as draft");
                            } catch (_) {
                                toast.error("Too late to undo");
                            }
                        }
                    },
                    duration: 10000
                });
            } else if (msg?.status === "scheduled") {
                toast.success("Email scheduled");
            } else {
                toast.success("Email sent");
            }
            onSent?.(msg);
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Send failed");
        } finally {
            setLoading(false);
        }
    };

    const handleDraft = async () => {
        setLoading(true);
        try {
            const res = await saveDraft(payload());
            if (res.status !== 200) throw new Error(res.message);
            toast.success("Draft saved");
            onSent?.(res.data);
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Could not save draft");
        } finally {
            setLoading(false);
        }
    };

    const handleAi = async (tone) => {
        syncHtmlFromEditor();
        const body = mode === "visual" && editorRef.current ? editorRef.current.innerHTML : html;
        setAiLoading(true);
        try {
            const res = await aiMailDraft({
                messageId: replyTo?._id,
                leadId,
                tone,
                extraInstructions: aiNote,
                currentHtml: body,
                currentSubject: subject
            });
            if (res.status !== 200) throw new Error(res.message);
            setSubject(res.data.subject || subject);
            setHtml(res.data.html || "");
            if (editorRef.current) editorRef.current.innerHTML = res.data.html || "";
            toast.success("AI draft ready — review before sending");
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "AI draft failed");
        } finally {
            setAiLoading(false);
        }
    };

    const onPickFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const res = await uploadMailFile(fd);
            if (res.status !== 200) throw new Error(res.message);
            setAttachments((prev) => [...prev, res.data]);
        } catch (err) {
            toast.error(err.response?.data?.message || "Upload failed");
        }
    };

    const addLibrary = (item) => {
        setAttachments((prev) => {
            if (prev.some((a) => a.path === item.path)) return prev;
            return [
                ...prev,
                {
                    filename: item.filename,
                    originalName: item.originalName,
                    contentType: item.contentType,
                    size: item.size,
                    path: item.path,
                    libraryId: item._id
                }
            ];
        });
    };

    const mailboxHint = useMemo(
        () => mailboxes.find((m) => m._id === mailboxId),
        [mailboxes, mailboxId]
    );

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="text-base">
                        {replyTo ? "Reply" : lead ? `Email ${lead.firstName || ""} ${lead.lastName || ""}`.trim() : "New email"}
                    </DialogTitle>
                </DialogHeader>

                {!mailboxes.length && (
                    <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        You need to add your own mailbox first. Open <strong>Inbox → Mailboxes</strong> and connect SMTP + IMAP.
                    </div>
                )}

                {recent.length > 0 && (
                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                        You already emailed this lead recently:{" "}
                        {recent.map((r) => `“${r.subject}”`).join(", ")}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-xs">From</Label>
                        <select
                            className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                            value={mailboxId}
                            onChange={(e) => setMailboxId(e.target.value)}
                        >
                            {mailboxes.map((m) => (
                                <option key={m._id} value={m._id}>
                                    {m.label} ({m.fromEmail || m.smtp?.username})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <Label className="text-xs">Template</Label>
                        <select
                            className="mt-1 w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                            value={templateId}
                            onChange={(e) => applyTemplate(e.target.value)}
                        >
                            <option value="">Choose a template…</option>
                            {templates.map((t) => (
                                <option key={t._id} value={t._id}>
                                    {t.category !== "General" ? `${t.category} · ` : ""}
                                    {t.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div>
                    <div className="flex items-center justify-between">
                        <Label className="text-xs">To</Label>
                        <button type="button" className="text-[11px] text-gray-500" onClick={() => setShowCc((v) => !v)}>
                            {showCc ? "Hide Cc" : "Cc"}
                        </button>
                    </div>
                    <Input className="mt-1 h-9" value={to} onChange={(e) => setTo(e.target.value)} placeholder="lead@email.com" />
                </div>
                {showCc && (
                    <div>
                        <Label className="text-xs">Cc</Label>
                        <Input className="mt-1 h-9" value={cc} onChange={(e) => setCc(e.target.value)} />
                    </div>
                )}
                <div>
                    <Label className="text-xs">Subject</Label>
                    <Input className="mt-1 h-9" value={subject} onChange={(e) => setSubject(e.target.value)} />
                </div>

                <div className="flex items-center gap-2 text-xs">
                    <button
                        type="button"
                        className={`px-2 py-1 rounded ${mode === "visual" ? "bg-gray-900 text-white" : "bg-gray-100"}`}
                        onClick={() => {
                            syncHtmlFromEditor();
                            setMode("visual");
                        }}
                    >
                        Write
                    </button>
                    <button
                        type="button"
                        className={`px-2 py-1 rounded ${mode === "source" ? "bg-gray-900 text-white" : "bg-gray-100"}`}
                        onClick={() => {
                            syncHtmlFromEditor();
                            setMode("source");
                        }}
                    >
                        HTML
                    </button>
                    <span className="text-gray-400 ml-auto">{"{{firstName}} {{lastName}} {{status}}"}</span>
                </div>

                {mode === "visual" ? (
                    <div
                        ref={editorRef}
                        className="min-h-[220px] max-h-[320px] overflow-y-auto rounded-md border border-input bg-white px-3 py-2 text-sm outline-none"
                        contentEditable
                        suppressContentEditableWarning
                        onBlur={syncHtmlFromEditor}
                    />
                ) : (
                    <Textarea className="min-h-[220px] font-mono text-xs" value={html} onChange={(e) => setHtml(e.target.value)} />
                )}

                <div className="flex flex-wrap items-center gap-2">
                    {TONES.map((t) => (
                        <Button key={t.id} type="button" variant="outline" size="sm" disabled={aiLoading} onClick={() => handleAi(t.id)}>
                            {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}
                            {t.label}
                        </Button>
                    ))}
                    <Input
                        className="h-8 max-w-[220px] text-xs"
                        placeholder="Tell AI what to say…"
                        value={aiNote}
                        onChange={(e) => setAiNote(e.target.value)}
                    />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <input ref={fileRef} type="file" className="hidden" onChange={onPickFile} />
                    <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                        <Paperclip className="h-3.5 w-3.5 mr-1" /> Attach
                    </Button>
                    {library.length > 0 && (
                        <select
                            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
                            defaultValue=""
                            onChange={(e) => {
                                const item = library.find((l) => l._id === e.target.value);
                                if (item) addLibrary(item);
                                e.target.value = "";
                            }}
                        >
                            <option value="">From library…</option>
                            {library.map((l) => (
                                <option key={l._id} value={l._id}>
                                    {l.originalName}
                                </option>
                            ))}
                        </select>
                    )}
                    {attachments.map((a, i) => (
                        <span key={`${a.filename}-${i}`} className="inline-flex items-center gap-1 text-xs bg-gray-100 rounded px-2 py-1">
                            {a.originalName || a.filename}
                            <button type="button" onClick={() => setAttachments((p) => p.filter((_, idx) => idx !== i))}>
                                <X className="h-3 w-3" />
                            </button>
                        </span>
                    ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t pt-3">
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={sendLater} onChange={(e) => setSendLater(e.target.checked)} />
                        <Clock className="h-4 w-4 text-gray-500" /> Send later
                    </label>
                    {sendLater && (
                        <Input type="datetime-local" className="h-9" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
                    )}
                    <label className="flex items-center gap-2 text-sm md:col-span-2">
                        <input type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} />
                        If no reply, send a follow-up
                    </label>
                    {followUp && (
                        <>
                            <div>
                                <Label className="text-xs">After (hours)</Label>
                                <Input type="number" min={1} className="h-9 mt-1" value={followHours} onChange={(e) => setFollowHours(e.target.value)} />
                            </div>
                            <div>
                                <Label className="text-xs">Follow-up subject</Label>
                                <Input className="h-9 mt-1" value={followSubject} onChange={(e) => setFollowSubject(e.target.value)} placeholder={`Re: ${subject}`} />
                            </div>
                            <div className="md:col-span-2">
                                <Label className="text-xs">Follow-up body (HTML)</Label>
                                <Textarea className="mt-1 text-sm" rows={3} value={followHtml} onChange={(e) => setFollowHtml(e.target.value)} />
                            </div>
                        </>
                    )}
                </div>

                {mailboxHint?.signature && (
                    <p className="text-[11px] text-gray-400">Signature from “{mailboxHint.label}” is added automatically on send.</p>
                )}

                <div className="flex justify-end gap-2 pt-1">
                    <Button type="button" variant="outline" onClick={handleDraft} disabled={loading}>
                        Save draft
                    </Button>
                    <Button type="button" onClick={handleSend} disabled={loading || !mailboxes.length} className="bg-red-600 hover:bg-red-700">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : sendLater ? <Clock className="h-4 w-4 mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                        {sendLater ? "Schedule" : "Send"}
                    </Button>
                </div>
                <p className="text-[11px] text-gray-400 text-right">Send waits 10 seconds so you can undo.</p>
            </DialogContent>
        </Dialog>
    );
}
